import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  modelClass, inventoryPolicyReferences, inventoryPolicyRuleCounts, InventoryPolicy, PolicyRule,
} from '/imports/api/inventoryPolicies';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { Input } from '/imports/ui/Input';
import { NumberInput } from '/imports/ui/NumberInput';
import { JsonField } from '/imports/ui/JsonField';

/** Rules must parse to an array of objects; the deep shape (keys, qty) is
 *  checked server-side at save so the error message has full context. */
export const validateRulesJson = (value: unknown): string | null => {
  if (!Array.isArray(value)) return 'Must be a JSON array, e.g. [{"sets": ["MSH"], "qty": 8}]';
  if (value.some((r) => !r || typeof r !== 'object' || Array.isArray(r))) return 'Each rule must be an object';
  return null;
};

const describeRule = (r: PolicyRule): string => {
  const parts: string[] = [];
  if (r.cardIds) parts.push(`${r.cardIds.length} card id${r.cardIds.length === 1 ? '' : 's'}`);
  if (r.sets) parts.push(`sets ${r.sets.join(', ')}`);
  if (r.format) parts.push(r.format);
  if (r.foil !== undefined) parts.push(r.foil ? 'foil' : 'non-foil');
  return parts.length ? parts.join(' + ') : 'every card';
};

const describeQtys = (r: PolicyRule): string =>
  `qty ${r.qty}${r.adQty !== undefined ? `, ad ${r.adQty}` : ''}`;

/**
 * Live per-rule hit counts (first-match-wins, so each count excludes cards
 * claimed by earlier rules). Counts run against the whole catalog; membership
 * is still the sell selection, so they are indicative.
 */
const RuleHitCounts = ({ rules }: { rules: PolicyRule[] | undefined }) => {
  const [state, setState] = useState<{ ok: boolean; error?: string; counts: number[] } | null>(null);

  const key = JSON.stringify(rules ?? []);
  useEffect(() => {
    if (!rules?.length) { setState(null); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      inventoryPolicyRuleCounts.call({ rules })
        .then((r) => { if (!cancelled) setState(r); })
        .catch((e) => { if (!cancelled) setState({ ok: false, error: `${e}`, counts: [] }); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [key]);

  if (!rules?.length) return null;
  return (
    <div className="rounded-card border border-soft bg-panel-alt p-3">
      <div className="eyebrow text-fg-3">Rule matches (first match wins)</div>
      {!state && <div className="mt-1 text-caption text-fg-3">Counting…</div>}
      {state && !state.ok && <div className="mt-1 text-caption text-danger">{state.error}</div>}
      {state?.ok && rules.map((r, i) => (
        <div key={i} className="mt-1 flex items-baseline justify-between gap-3 text-ui">
          <span>{i + 1}. {describeRule(r)} → {describeQtys(r)}</span>
          <span className="font-mono text-caption text-fg-3">{state.counts[i]?.toLocaleString() ?? '—'} cards</span>
        </div>
      ))}
      <div className="mt-2 text-caption text-fg-3">
        Counts are against the whole catalog — which cards a bot actually stocks is still decided by its sell selection.
      </div>
    </div>
  );
};

/** Who uses this policy. Editing it changes every referencing fleet and bot at
 *  once, so the blast radius belongs on the page where the edit happens. */
const ReferencedBy = ({ policyId }: { policyId?: string }) => {
  const [refs, setRefs] = useState<{ fleets: { _id: string; name: string }[]; bots: { _id: string; username: string }[] } | null>(null);

  useEffect(() => {
    if (!policyId) { setRefs(null); return; }
    let cancelled = false;
    inventoryPolicyReferences.call({ policyId })
      .then((r) => { if (!cancelled) setRefs(r); })
      .catch(() => { if (!cancelled) setRefs(null); });
    return () => { cancelled = true; };
  }, [policyId]);

  if (!policyId) return null;
  const empty = refs && !refs.fleets.length && !refs.bots.length;
  return (
    <div className="rounded-card border border-soft bg-panel-alt p-3">
      <div className="eyebrow text-fg-3">Referenced by</div>
      {!refs && <div className="mt-1 text-caption text-fg-3">Checking…</div>}
      {empty && <div className="mt-1 text-caption text-fg-3">Nothing yet. Point a fleet or a bot config at this policy to use it.</div>}
      {refs?.fleets.map((f) => (
        <div key={f._id} className="mt-1 text-ui">
          <Link className="text-primary hover:underline" to={`/fleets/${f._id}`}>{f.name}</Link>
          <span className="text-caption text-fg-3"> (fleet)</span>
        </div>
      ))}
      {refs?.bots.map((b) => (
        <div key={b._id} className="mt-1 text-ui">
          <Link className="text-primary hover:underline" to={`/bots/${b._id}`}>{b.username}</Link>
          <span className="text-caption text-fg-3"> (bot config)</span>
        </div>
      ))}
      {!empty && refs && (
        <div className="mt-2 text-caption text-warn">
          Saving changes here updates all of the above immediately.
        </div>
      )}
    </div>
  );
};

const currentRules = (update: any, document: any): PolicyRule[] | undefined =>
  ('rules' in ((update ?? {}) as object) ? update.rules : document?.rules) ?? undefined;

export const InventoryPolicyPage = () => (
  <ModelFormPage
    modelClass={modelClass as any}
    permission={(user) => !!user?.isSuperUser}
    formSpec={() => [
      [{ field: 'name', label: 'Name', input: { type: Input }, tip: 'Unique policy name, e.g. New Set Deep Stock.' }],
      [{ field: 'description', label: 'Description', input: { type: Input } }],
      [{
        field: 'defaultQty', label: 'Default Target Qty', input: { type: NumberInput },
        tip: 'Copies to CARRY when no rule matches. Blank falls through to the fleet/Settings sellCap, then the bot\'s stocksize (8).',
      }, {
        field: 'defaultAdQty', label: 'Default Advertised Qty', input: { type: NumberInput },
        tip: 'Max copies OFFERED for sale when the matched rule has no adQty. Cannot exceed the target. Blank falls through to sellCap, then shelfsize (4).',
      }],
      [{
        field: 'rules',
        input: (setUpdate, _update, document) => (
          <JsonField<InventoryPolicy>
            field="rules"
            label="Rules"
            tip={'JSON array, first match wins; predicates AND within a rule. Keys: cardIds, sets, format ("pauper"|"legacy"), foil, qty (target: copies to carry), adQty (max offered for sale, <= qty). '
              + 'qty 0 = don\'t restock and deplete held copies. E.g. [{"sets":["HOB"],"qty":8,"adQty":4},{"foil":true,"qty":1}]'}
            initial={(document as any)?.rules}
            validate={validateRulesJson}
            emptyValue={null}
            setUpdate={setUpdate as any}
            className="h-32! font-mono text-caption"
          />
        ),
      }],
      [{
        field: 'rules',
        input: (_setUpdate, update, document) => (
          <RuleHitCounts rules={currentRules(update, document)} />
        ),
      }],
      [{
        field: 'rules',
        input: (_setUpdate, _update, document) => (
          <ReferencedBy policyId={(document as any)?._id} />
        ),
      }],
    ]}
  />
);
